import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { cn } from '../../lib/cn';
import { loadContent } from '../../lib/content';
import { formatDate } from '../../lib/format';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { useRuleSet } from '../../hooks/useRuleSet';
import { Dialog, DialogClose } from '../ui/Dialog';
import { ExternalAnchor } from '../ui/Link';
import { Spinner } from '../ui/Spinner';
import { Alert } from '../ui/Alert';
import { Markdown } from './Markdown';
import { SpecialistLink } from './SpecialistLink';

export interface InfoButtonProps {
  /** Canonical explanation id (ARCHITECTURE §7), e.g. "iht-annual-exemption". */
  ruleId: string;
  /** Accessible name for the button. Default "What does this mean?". Make it specific where you can. */
  label?: string;
  /** Show the label next to the icon instead of icon-only. */
  showLabel?: boolean;
  className?: string;
}

/**
 * ⓘ button that opens the plain-English explanation card for a rule (FR-12.1): what the rule is, why it
 * matters, what you can do, the official source, plus the rule-set version and last-verified date.
 * Content comes from @seventide/content (loaded on first open); rule figures are filled in from the
 * active rule set for the workspace's nation.
 */
export function InfoButton({ ruleId, label = 'What does this mean?', showLabel, className }: InfoButtonProps) {
  const [open, setOpen] = useState(false);
  const ws = useOptionalWorkspace();
  const nation = ws?.workspace.nation;
  const content = useQuery({ queryKey: ['content'], queryFn: loadContent, staleTime: Infinity, enabled: open });
  const ruleSet = useRuleSet(nation, open);
  // Content placeholders use the flattened keys from @seventide/rules `toContentValues` (e.g.
  // `values.annualExemptionPence`), not the nested rule-set `values` (platform/rules fix).
  const contentValues = useQuery({
    queryKey: ['rules', 'content-values', nation ?? 'england', ruleSet.data?.version, ruleSet.data?.signature],
    queryFn: async () => {
      const rules = await import('@seventide/rules');
      return rules.toContentValues(rules.parseRuleSet(ruleSet.data!.payload)) as unknown as Record<string, unknown>;
    },
    staleTime: Infinity,
    enabled: open && Boolean(ruleSet.data),
  });

  const explanation = content.data
    ? contentValues.data
      ? content.data.renderExplanation(ruleId, { values: contentValues.data })
      : content.data.renderExplanation(ruleId, {}, { onMissing: 'bracket' })
    : undefined;

  const trigger = (
    <button
      type="button"
      aria-label={showLabel ? undefined : label}
      className={cn(
        'inline-flex min-h-11 min-w-11 items-center justify-center gap-1 rounded-full text-primary hover:bg-primary-soft',
        showLabel && 'px-2 font-bold underline underline-offset-4',
        className,
      )}
    >
      <Info aria-hidden="true" className="h-5 w-5" />
      {showLabel && <span>{label}</span>}
    </button>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={explanation?.title ?? 'Explanation'}
      size="md"
      footer={<DialogClose>Close</DialogClose>}
    >
      {content.isLoading || (open && (ruleSet.isLoading || contentValues.isLoading)) ? (
        <Spinner label="Loading explanation" />
      ) : content.isError ? (
        <Alert tone="warning" title="We couldn't load this explanation">
          Check your connection and try again.
        </Alert>
      ) : !explanation ? (
        <Alert tone="info" title="This explanation is coming soon">
          We are still writing the plain-English card for this rule. In the meantime, the official GOV.UK guidance
          is the best source.
        </Alert>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-lg">{explanation.summary}</p>
          <ExplanationSection title="What the rule is" body={explanation.sections.whatTheRuleIs} />
          <ExplanationSection title="Why it matters" body={explanation.sections.whyItMatters} />
          <ExplanationSection title="What you can do" body={explanation.sections.whatYouCanDo} />
          <section className="flex flex-col gap-2">
            <h3 className="text-lg font-bold">Official source</h3>
            {explanation.sections.officialSource && <Markdown text={explanation.sections.officialSource} baseHeading={4} />}
            {explanation.sources.length > 0 && (
              <ul className="flex flex-col gap-1">
                {explanation.sources.map((s) => (
                  <li key={s.url}>
                    <ExternalAnchor href={s.url}>{s.label}</ExternalAnchor>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {explanation.specialist && <SpecialistLink topic={explanation.specialist} />}
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-surface-muted p-3 text-sm">
            <dt className="font-bold">Rules version</dt>
            <dd>{ruleSet.data?.version ?? ws?.workspace.ruleSetVersion ?? 'Not available'}</dd>
            <dt className="font-bold">Rules last verified</dt>
            <dd>{ruleSet.data?.verifiedOn ? formatDate(ruleSet.data.verifiedOn) : 'Not available'}</dd>
            <dt className="font-bold">Explanation reviewed</dt>
            <dd>{formatDate(explanation.lastReviewed)}</dd>
          </dl>
        </div>
      )}
    </Dialog>
  );
}

function ExplanationSection({ title, body }: { title: string; body: string }) {
  if (!body.trim()) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-lg font-bold">{title}</h3>
      <Markdown text={body} baseHeading={4} />
    </section>
  );
}
